import React, { useState, useEffect, Fragment } from 'react';
import {
  CreditCard,
  Calendar,
  FileText,
  DollarSign,
  Clock,
  Settings,
  Receipt,
  ShoppingCart,
  RefreshCw,
  Download,
  X,
  AlertTriangle,
  Building2,
  Trash2
} from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/apiService';
import { loadStripe, Stripe } from '@stripe/stripe-js';

// NACHA-mandated authorization disclosure. Persisted verbatim in the
// ach_authorizations table on save — change the text here and the audit trail
// reflects exactly what each customer agreed to.
const NACHA_AUTHORIZATION_TEXT =
  'I authorize ITP360 to electronically debit my bank account for amounts owed on my account ' +
  'with ITP360, including future recurring invoice charges. This authorization remains in effect ' +
  'until I revoke it in writing or by removing the bank account on this page. I understand that ' +
  'returns or NSF failures may result in additional fees, and that disputes must be raised within ' +
  '60 days under federal Regulation E.';

// Recurring ACH consent is captured by the bank-connect authorization
// (type='recurring'); no separate enable-time mandate is collected.

interface BillingAccount {
  balance: string;
  past_due_balance: string;
  credits: string;
  automatic_payments: boolean;
  billing_cycle_month_date: number;
  payment_method: string;
  days_past_due: number;
  // 'knack' accounts bill through a Stripe account that takes no typed-in
  // card numbers: their card is saved on Stripe's own page (card setup link)
  // and credit_card_info is filled from that saved card.
  billing_brand?: string;
  credit_card_info: {
    last_4: string | null;
    type: string | null;
    first_name: string | null;
    last_name: string | null;
    exp_month: string | null;
    exp_year: string | null;
  };
  // payment_method (declared above) doubles as the autopay selector when
  // automatic_payments is on: 'ach' debits the saved bank, anything else
  // charges the card.
}

interface Statement {
  _id: string;
  created: string;
}

interface PaymentFormData {
  amount: string;
  cc_first_name: string;
  cc_last_name: string;
  cc_num: string;
  cc_exp_month: string;
  cc_exp_year: string;
  cc_type: string;
}

const Billing: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'statements' | 'payments' | 'orders' | 'payment' | 'settings'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);
  const [statements, setStatements] = useState<Statement[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [downloadingStatement, setDownloadingStatement] = useState<string | null>(null);
  const [autoPaymentsLoading, setAutoPaymentsLoading] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardUpdateLoading, setCardUpdateLoading] = useState(false);
  const [cardSetupLoading, setCardSetupLoading] = useState(false);
  const itemsPerPage = 20;
  
  // Payments history state
  const [payments, setPayments] = useState<any[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsCurrentPage, setPaymentsCurrentPage] = useState(1);
  
  // Orders history state
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [services, setServices] = useState<any[]>([]);
  
  // Payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [useStoredCard, setUseStoredCard] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState<PaymentFormData>({
    amount: '',
    cc_first_name: '',
    cc_last_name: '',
    cc_num: '',
    cc_exp_month: '01',
    cc_exp_year: (new Date().getFullYear() + 1).toString(),
    cc_type: 'visa'
  });

  // ACH (Stripe Financial Connections) state
  const [paymentMethodKind, setPaymentMethodKind] = useState<'card' | 'ach'>('card');
  const [achInfo, setAchInfo] = useState<{
    payment_method_id: string;
    bank_name: string | null;
    account_last_4: string | null;
    account_holder_type: string | null;
    verified_at: string;
  } | null>(null);
  const [achNachaChecked, setAchNachaChecked] = useState(false);
  const [achConnectLoading, setAchConnectLoading] = useState(false);
  const [achPayLoading, setAchPayLoading] = useState(false);
  const [achRemoveLoading, setAchRemoveLoading] = useState(false);
  const [achPaymentSuccess, setAchPaymentSuccess] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);

  // Auto-pay (Settings tab) — "set as default" interaction state.
  const [defaultMethodLoading, setDefaultMethodLoading] = useState<'card' | 'ach' | null>(null);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user details for account billing_api_id
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      
      // The billing account ID is in account.billing_api_id from login response
      const accountId = userDetails.account?.billing_api_id;

      if (!accountId) {
        throw new Error('No billing_api_id found in user account data');
      }

      // Load billing account data
      const accountResponse = await apiService.getBillingAccount(accountId);
      let account = accountResponse.result;
      if (account?.billing_brand === 'knack') {
        // The card lives only in Stripe; show the one saved on Stripe's page.
        let card: any = null;
        try {
          const cardResponse = await apiService.get(`/billing/account/${accountId}/payments/card-setup`);
          card = cardResponse?.result?.card || null;
        } catch (cardErr) {
          console.warn('Saved card not available:', cardErr);
        }
        account = {
          ...account,
          credit_card_info: {
            ...(account.credit_card_info || {}),
            last_4: card?.last_4 || null,
            type: card?.brand || null,
            exp_month: card?.exp_month ? String(card.exp_month).padStart(2, '0') : null,
            exp_year: card?.exp_year ? String(card.exp_year) : null,
          },
        };
      }
      setBillingAccount(account);

      // Load services for monthly estimate
      const servicesResponse = await apiService.getServices();
      if (servicesResponse.result) {
        setServices(servicesResponse.result);
      }

      // Load saved ACH info (separate endpoint — non-fatal if it 404s for old accounts).
      try {
        const achResponse = await apiService.getACHInfo(accountId);
        setAchInfo(achResponse?.result || null);
      } catch (achErr) {
        // Endpoint may not be live yet in this environment — fail soft.
        console.warn('ACH info not available:', achErr);
        setAchInfo(null);
      }

      // Pre-load Stripe.js so the FC popup is instant on click. Only needs to
      // happen once per session.
      if (!stripeInstance) {
        try {
          const keyResp = await apiService.getStripePublishableKey(accountId);
          const pk = keyResp?.result?.publishable_key;
          if (pk) {
            const stripe = await loadStripe(pk);
            setStripeInstance(stripe);
          }
        } catch (keyErr) {
          console.warn('Stripe.js not initialized — ACH will be disabled:', keyErr);
        }
      }

      // Load statements if on statements tab
      if (activeTab === 'statements') {
        loadStatements(accountId);
      }

    } catch (err: any) {
      console.error('Error loading billing data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  // ---- ACH (Stripe Financial Connections) handlers ----

  const handleACHConnect = async () => {
    if (!achNachaChecked) {
      setError('Please check the authorization box to continue.');
      return;
    }
    if (!stripeInstance) {
      setError('Stripe is not ready yet. Please refresh the page and try again.');
      return;
    }
    setAchConnectLoading(true);
    setError(null);
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;

      const sessionResp = await apiService.createACHConnectSession(accountId, window.location.href);
      const clientSecret = sessionResp?.result?.client_secret;
      if (!clientSecret) throw new Error('Could not start bank connect session');

      // Get customer name/email for the mandate billing details. Falls back
      // to placeholders if not in localStorage — Stripe requires both fields.
      const userDetails2 = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const billingName =
        userDetails2?.account?.name ||
        `${userDetails2?.first_name || ''} ${userDetails2?.last_name || ''}`.trim() ||
        'Account Holder';
      const billingEmail = userDetails2?.email || 'noreply@itp360.com';

      // Use SetupIntent flow — collectBankAccountForSetup opens the Stripe
      // Financial Connections popup AND auto-creates+attaches a PaymentMethod,
      // returning setupIntent.payment_method as the new pm_xxx ID. The older
      // collectFinancialConnectionsAccounts call does NOT return PMs directly.
      const setupResult = await (stripeInstance as any).collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: billingName,
              email: billingEmail,
            },
          },
        },
      });

      if (setupResult?.error) {
        throw new Error(setupResult.error.message || 'Bank connection failed');
      }

      const setupIntent = setupResult?.setupIntent;
      // `next_action` indicates the SetupIntent needs another step — most
      // common cause here is the customer closed the FC popup without
      // completing it. Treat as a soft cancel.
      if (!setupIntent || setupIntent.status === 'requires_action') {
        setError('Bank connection was cancelled or could not complete. Please try again.');
        return;
      }

      // Some flows leave the SetupIntent in `requires_confirmation` — confirm
      // it client-side to finalize.
      let finalSetupIntent = setupIntent;
      if (setupIntent.status === 'requires_confirmation') {
        const confirmed = await (stripeInstance as any).confirmUsBankAccountSetup(clientSecret);
        if (confirmed?.error) {
          throw new Error(confirmed.error.message || 'Bank confirmation failed');
        }
        finalSetupIntent = confirmed?.setupIntent || setupIntent;
      }

      const paymentMethodId =
        (finalSetupIntent.payment_method && (finalSetupIntent.payment_method as any).id) ||
        finalSetupIntent.payment_method;
      if (!paymentMethodId) {
        // Diagnostic — log the actual SetupIntent so we can see what came back.
        console.error('SetupIntent returned with no payment_method:', finalSetupIntent);
        throw new Error('Bank linked but no PaymentMethod returned by Stripe');
      }

      await apiService.saveACHPaymentMethod(accountId, paymentMethodId, {
        text: NACHA_AUTHORIZATION_TEXT,
        type: 'recurring',
      });

      // Reload ACH info to show the saved bank
      const achResp = await apiService.getACHInfo(accountId);
      setAchInfo(achResp?.result || null);
      setAchNachaChecked(false);
    } catch (err: any) {
      console.error('ACH connect error:', err);
      setError(err.response?.data?.message || err.message || 'Bank connection failed');
    } finally {
      setAchConnectLoading(false);
    }
  };

  const handleACHPay = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError('Enter a payment amount greater than zero.');
      return;
    }
    if (!achInfo) {
      setError('No saved bank account.');
      return;
    }
    setAchPayLoading(true);
    setError(null);
    setAchPaymentSuccess(null);
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;

      await apiService.payWithACH(accountId, paymentAmount);
      setAchPaymentSuccess(
        `ACH debit of ${formatCurrency(paymentAmount)} submitted. Funds typically clear in 3-5 business days; we'll email you when settlement completes.`
      );
      await loadBillingData();
      setShowPaymentForm(false);
      setPaymentAmount('');
    } catch (err: any) {
      console.error('ACH pay error:', err);
      setError(err.response?.data?.message || err.message || 'ACH payment failed');
    } finally {
      setAchPayLoading(false);
    }
  };

  const handleACHRemove = async () => {
    if (!window.confirm('Remove this saved bank account? You will need to re-link it for future ACH payments.')) {
      return;
    }
    setAchRemoveLoading(true);
    setError(null);
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;
      await apiService.removeACHPaymentMethod(accountId);
      setAchInfo(null);
    } catch (err: any) {
      console.error('ACH remove error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to remove bank');
    } finally {
      setAchRemoveLoading(false);
    }
  };

  const loadPaymentsHistory = async () => {
    try {
      setPaymentsLoading(true);
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;
      
      if (!accountId) {
        setError('No billing account found');
        return;
      }

      const paymentsData = await apiService.getBillingPayments(accountId);
      setPayments(paymentsData.result || []);
      
    } catch (err: any) {
      console.error('Payments loading error:', err);
      setError('Failed to load payments history');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setOrdersLoading(true);
      const ordersData = await apiService.getOrders();
      setOrders(ordersData.result || []);
    } catch (err: any) {
      console.error('Orders loading error:', err);
      setError('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadOrderDetails = async (orderId: string) => {
    try {
      setOrderDetailsLoading(true);
      const orderData = await apiService.getOrderDetails(orderId);
      setSelectedOrder(orderData.result);
      setShowOrderModal(true);
    } catch (err: any) {
      console.error('Order details loading error:', err);
      setError('Failed to load order details');
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const loadStatements = async (accountId: string) => {
    try {
      setStatementsLoading(true);
      const statementsResponse = await apiService.getBillingStatements(accountId);
      setStatements(statementsResponse.result || []);
    } catch (err: any) {
      console.error('Error loading statements:', err);
      setError('Failed to load statements');
    } finally {
      setStatementsLoading(false);
    }
  };

  const calculateEstimatedMonthly = () => {
    if (!services.length) return 0;
    return services.reduce((total, service) => total + (service.price || 0), 0);
  };

  const calculateNextInvoiceDate = () => {
    if (!billingAccount?.billing_cycle_month_date) return 'Not set';
    
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const cycleDay = billingAccount.billing_cycle_month_date;
    
    // Create next invoice date
    let nextInvoiceDate = new Date(currentYear, currentMonth, cycleDay);
    
    // If the cycle day has already passed this month, move to next month
    if (nextInvoiceDate <= today) {
      nextInvoiceDate = new Date(currentYear, currentMonth + 1, cycleDay);
    }
    
    return nextInvoiceDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const downloadStatement = async (statementId: string, createdDate: string) => {
    try {
      setDownloadingStatement(statementId);
      
      // Use the same endpoint as the old project: /crm/accounts/statements/{statementId}
      const response = await apiService.get(`/crm/accounts/statements/${statementId}`);
      
      // Create download link with formatted date as filename
      const formattedDate = new Date(createdDate).toLocaleDateString('en-US').replace(/\//g, '-');
      const link = document.createElement('a');
      link.download = `statement-${formattedDate}.pdf`;
      link.href = `data:application/pdf;base64,${response.result}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error downloading statement:', err);
      setError('Failed to download statement');
    } finally {
      setDownloadingStatement(null);
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    // For ACH, dispatch to the ACH-specific handler — the card form fields aren't required.
    if (paymentMethodKind === 'ach') {
      return handleACHPay();
    }

    setPaymentLoading(true);

    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;

      if (isKnack && !billingAccount?.credit_card_info?.last_4) {
        setError('Save a card first. You enter it on our secure payment page.');
        return;
      }

      if ((useStoredCard || isKnack) && billingAccount?.credit_card_info?.last_4) {
        // Use stored card
        await apiService.post(`/billing/account/${accountId}/payments/creditcard-onfile`, {
          amount: paymentAmount
        });
      } else {
        // Use new card
        await apiService.makeCreditCardPayment(accountId, {
          amount: paymentAmount,
          cc_first_name: paymentFormData.cc_first_name,
          cc_last_name: paymentFormData.cc_last_name,
          cc_num: paymentFormData.cc_num,
          cc_exp_month: paymentFormData.cc_exp_month,
          cc_exp_year: paymentFormData.cc_exp_year,
          cc_type: paymentFormData.cc_type,
        });
      }
      
      // Success - reload billing data and close form
      await loadBillingData();
      setShowPaymentForm(false);
      setPaymentAmount('');
      setPaymentFormData({
        amount: '',
        cc_first_name: '',
        cc_last_name: '',
        cc_num: '',
        cc_exp_month: '01',
        cc_exp_year: (new Date().getFullYear() + 1).toString(),
        cc_type: 'visa'
      });
      
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || 'Payment failed');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Resolves which saved method auto-pay will charge. Single-method accounts
  // bypass the explicit choice. Falls back to 'card' for legacy accounts that
  // pre-date the payment_method selector.
  const getEffectiveDefaultMethod = (): 'card' | 'ach' | null => {
    const hasCard = !!billingAccount?.credit_card_info?.last_4;
    const hasAch = !!achInfo;
    if (!hasCard && !hasAch) return null;
    if (!hasAch) return 'card';
    if (!hasCard) return 'ach';
    return billingAccount?.payment_method === 'ach' ? 'ach' : 'card';
  };

  const updateAutoPayments = async (enabled: boolean) => {
    const effectiveDefault = getEffectiveDefaultMethod();

    if (enabled && !effectiveDefault) {
      setError('Please add a payment method before enabling automatic payments');
      return;
    }

    try {
      setAutoPaymentsLoading(true);
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;

      // Persist the toggle plus which saved method the billing cron charges.
      // ACH's recurring consent is the authorization captured at bank-connect.
      await apiService.updateBillingAccount(accountId, {
        automatic_payments: enabled,
        ...(enabled && effectiveDefault ? { payment_method: effectiveDefault } : {}),
      });

      await loadBillingData();
    } catch (err: any) {
      console.error('Auto payments update error:', err);
      setError('Failed to update auto payments');
    } finally {
      setAutoPaymentsLoading(false);
    }
  };

  const setDefaultPaymentMethod = async (kind: 'card' | 'ach') => {
    try {
      setDefaultMethodLoading(kind);
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;

      await apiService.updateBillingAccount(accountId, {
        payment_method: kind
      });

      await loadBillingData();
    } catch (err: any) {
      console.error('Default payment method update error:', err);
      setError('Failed to update default payment method');
    } finally {
      setDefaultMethodLoading(null);
    }
  };

  const isKnack = billingAccount?.billing_brand === 'knack';

  // Knack cards are entered on Stripe's own page, never in this form.
  const openCardForm = async () => {
    if (!isKnack) {
      setShowCardModal(true);
      return;
    }
    try {
      setCardSetupLoading(true);
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;
      const response = await apiService.post(`/billing/account/${accountId}/payments/card-setup`, {});
      const url = response?.result?.url;
      if (!url) throw new Error('No card setup link returned');
      window.location.href = url;
    } catch (err: any) {
      console.error('Card setup link error:', err);
      setError(err.response?.data?.message || 'Could not open the secure card page');
      setCardSetupLoading(false);
    }
  };

  const updateCreditCard = async (cardData: any) => {
    try {
      setCardUpdateLoading(true);
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;
      
      await apiService.updateBillingAccount(accountId, {
        credit_card_info: cardData
      });
      
      await loadBillingData();
      setShowCardModal(false);
    } catch (err: any) {
      console.error('Credit card update error:', err);
      setError('Failed to update credit card');
    } finally {
      setCardUpdateLoading(false);
    }
  };

  const removeCreditCard = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account?.billing_api_id;
      
      // Use the specific removeCreditCard method that tries DELETE first, then PATCH fallback
      await apiService.removeCreditCard(accountId);
      
      await loadBillingData();
    } catch (err: any) {
      console.error('Credit card removal error:', err);
      setError('Failed to remove credit card');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-red-800 font-semibold">Error Loading Billing Information</h3>
          </div>
          <p className="text-red-600 mt-1">{error}</p>
          <button
            onClick={loadBillingData}
            className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
            <nav className="mt-2 text-sm text-gray-500">
              <span>Finance</span> / <span className="text-blue-600">Billing</span>
            </nav>
          </div>
          <button
            onClick={loadBillingData}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <RefreshCw className="h-4 w-4 mr-2 text-gray-600" />
            <span className="text-gray-700 font-medium">Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Current Balance */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-gray-900">
                    {formatCurrency(billingAccount?.balance || '0')}
                  </p>
                  <p className="text-gray-600 font-medium mt-1">Current Balance</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Estimated Monthly Services */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(calculateEstimatedMonthly())}
                  </p>
                  <p className="text-gray-600 font-medium mt-1">Monthly Services</p>
                  <p className="text-gray-500 text-sm mt-1">Before taxes, etc.</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Receipt className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* Next Invoice Date */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {calculateNextInvoiceDate()}
                  </p>
                  <p className="text-gray-600 font-medium mt-1">Next Invoice</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {billingAccount?.billing_cycle_month_date ? `${billingAccount.billing_cycle_month_date}${billingAccount.billing_cycle_month_date === 1 ? 'st' : billingAccount.billing_cycle_month_date === 2 ? 'nd' : billingAccount.billing_cycle_month_date === 3 ? 'rd' : 'th'} of each month` : 'Not set'}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', label: 'Overview', icon: DollarSign },
                  { id: 'statements', label: 'Statements', icon: FileText },
                  { id: 'payments', label: 'Payment History', icon: Receipt },
                  { id: 'orders', label: 'Orders', icon: ShoppingCart },
                  { id: 'payment', label: 'Make Payment', icon: CreditCard },
                  { id: 'settings', label: 'Auto Payments', icon: Settings },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id as any);
                      if (tab.id === 'statements') {
                        const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
                        const accountId = userDetails.account?.billing_api_id;
                        if (accountId) loadStatements(accountId);
                      } else if (tab.id === 'payments') {
                        loadPaymentsHistory();
                      } else if (tab.id === 'orders') {
                        loadOrders();
                      }
                    }}
                    className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <tab.icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Payment Method</span>
                          <span className="font-semibold text-gray-900 capitalize">
                            {billingAccount?.payment_method || 'Not set'}
                          </span>
                        </div>
                      </div> */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Auto Payments</span>
                          <span className={`font-semibold ${billingAccount?.automatic_payments ? 'text-green-600' : 'text-red-600'}`}>
                            {billingAccount?.automatic_payments ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                      {billingAccount?.credits && parseFloat(billingAccount.credits) > 0 && (
                        <div className="p-4 bg-green-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-green-600">Account Credits</span>
                            <span className="font-semibold text-green-600">
                              {formatCurrency(billingAccount.credits)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'statements' && (
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Billing Statements</h3>
                      {statements.length > 0 && (
                        <p className="text-sm text-gray-500 mt-0.5">{statements.length} statement{statements.length !== 1 ? 's' : ''} available</p>
                      )}
                    </div>
                  </div>

                  {statementsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="large" />
                    </div>
                  ) : statements.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No statements found</p>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="divide-y divide-gray-100">
                          {statements
                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                            .map((statement, index) => {
                              const d = new Date(statement.created);
                              const month = d.toLocaleDateString('en-US', { month: 'short' });
                              const day = d.getDate().toString().padStart(2, '0');
                              const year = d.getFullYear();
                              return (
                                <div
                                  key={statement._id}
                                  className={`flex items-center justify-between px-5 py-4 transition-all duration-150 hover:bg-gray-50 group ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center flex-shrink-0">
                                      <FileText className="h-4.5 w-4.5 text-blue-600" />
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-900 font-mono tracking-tight block">{month} {day}, {year}</span>
                                      <span className="text-xs text-gray-400">PDF Statement</span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => downloadStatement(statement._id, statement.created)}
                                    disabled={downloadingStatement === statement._id}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-600 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                                  >
                                    {downloadingStatement === statement._id ? (
                                      <>
                                        <LoadingSpinner size="small" />
                                        <span>Downloading...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Download className="h-3.5 w-3.5" />
                                        <span>Download</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      {/* Pagination */}
                      {Math.ceil(statements.length / itemsPerPage) > 1 && (
                        <div className="mt-6 flex items-center justify-between">
                          <div className="text-sm text-gray-700">
                            Showing <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, statements.length)}</span> to{' '}
                            <span className="font-medium">{Math.min(currentPage * itemsPerPage, statements.length)}</span> of{' '}
                            <span className="font-medium">{statements.length}</span> results
                          </div>
                          
                          <nav className="flex items-center space-x-1">
                            <button
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Previous
                            </button>
                            
                            {(() => {
                              const totalPages = Math.ceil(statements.length / itemsPerPage);
                              const pages = [];
                              const showPages = 5;
                              let start = Math.max(1, currentPage - Math.floor(showPages / 2));
                              let end = Math.min(totalPages, start + showPages - 1);

                              if (end - start + 1 < showPages) {
                                start = Math.max(1, end - showPages + 1);
                              }

                              // Add first page
                              if (start > 1) {
                                pages.push(1);
                                if (start > 2) pages.push('...');
                              }

                              // Add visible pages
                              for (let i = start; i <= end; i++) {
                                pages.push(i);
                              }

                              // Add last page
                              if (end < totalPages) {
                                if (end < totalPages - 1) pages.push('...');
                                pages.push(totalPages);
                              }

                              return pages.map((page, index) => (
                                <button
                                  key={index}
                                  onClick={() => typeof page === 'number' && setCurrentPage(page)}
                                  disabled={page === '...'}
                                  className={`relative inline-flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                                    currentPage === page
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : page === '...'
                                      ? 'bg-white text-gray-400 border-gray-300 cursor-default'
                                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                                  } border`}
                                >
                                  {page}
                                </button>
                              ));
                            })()}
                            
                            <button
                              onClick={() => setCurrentPage(Math.min(Math.ceil(statements.length / itemsPerPage), currentPage + 1))}
                              disabled={currentPage === Math.ceil(statements.length / itemsPerPage)}
                              className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Next
                            </button>
                          </nav>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'payment' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Make a Payment</h3>
                  
                  {!showPaymentForm ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Current Balance</h4>
                        <p className="text-3xl font-bold text-gray-900 mb-4">
                          {formatCurrency(billingAccount?.balance || '0')}
                        </p>
                        <div className="space-y-3">
                          <button
                            onClick={() => {
                              setPaymentAmount(billingAccount?.balance || '0');
                              setShowPaymentForm(true);
                            }}
                            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                          >
                            Pay Full Balance
                          </button>
                          <button
                            onClick={() => {
                              setPaymentAmount('');
                              setShowPaymentForm(true);
                            }}
                            className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                          >
                            Pay Custom Amount
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handlePayment} className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold text-gray-900">Payment Details</h4>
                        <button
                          type="button"
                          onClick={() => setShowPaymentForm(false)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                          required
                        />
                      </div>

                      {/* Payment method toggle: Credit Card vs Bank (ACH) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Method
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setPaymentMethodKind('card')}
                            className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                              paymentMethodKind === 'card'
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <CreditCard className="h-5 w-5 mr-2" />
                            <span className="font-medium">Credit Card</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPaymentMethodKind('ach')}
                            className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                              paymentMethodKind === 'ach'
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Building2 className="h-5 w-5 mr-2" />
                            <span className="font-medium">Bank Account (ACH)</span>
                          </button>
                        </div>
                        {paymentMethodKind === 'ach' && (
                          <p className="text-xs text-gray-500 mt-2">
                            ACH payments typically clear in 3-5 business days. No card fees apply.
                          </p>
                        )}
                      </div>

                      {paymentMethodKind === 'card' && isKnack && (
                        billingAccount?.credit_card_info?.last_4 ? (
                          <p className="text-sm font-medium text-gray-700">
                            Charged to your saved card ending in {billingAccount.credit_card_info.last_4}
                          </p>
                        ) : (
                          <div className="flex items-center justify-between p-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                            <p className="text-sm text-gray-600">Save a card first. You enter it on our secure payment page.</p>
                            <button
                              type="button"
                              onClick={openCardForm}
                              disabled={cardSetupLoading}
                              className="ml-3 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              {cardSetupLoading ? 'Opening...' : 'Save a card'}
                            </button>
                          </div>
                        )
                      )}

                      {paymentMethodKind === 'card' && !isKnack && billingAccount?.credit_card_info?.last_4 && (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="useStoredCard"
                              checked={useStoredCard}
                              onChange={(e) => setUseStoredCard(e.target.checked)}
                              className="h-4 w-4 text-blue-600 rounded border-gray-300"
                            />
                            <label htmlFor="useStoredCard" className="text-sm font-medium text-gray-700">
                              Use stored card ending in {billingAccount.credit_card_info.last_4}
                            </label>
                          </div>
                        </div>
                      )}

                      {paymentMethodKind === 'card' && !useStoredCard && !isKnack && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name
                              </label>
                              <input
                                type="text"
                                value={paymentFormData.cc_first_name}
                                onChange={(e) => setPaymentFormData({...paymentFormData, cc_first_name: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name
                              </label>
                              <input
                                type="text"
                                value={paymentFormData.cc_last_name}
                                onChange={(e) => setPaymentFormData({...paymentFormData, cc_last_name: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Card Number
                            </label>
                            <input
                              type="text"
                              value={paymentFormData.cc_num}
                              onChange={(e) => setPaymentFormData({...paymentFormData, cc_num: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="1234 5678 9012 3456"
                              required
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Month
                              </label>
                              <select
                                value={paymentFormData.cc_exp_month}
                                onChange={(e) => setPaymentFormData({...paymentFormData, cc_exp_month: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              >
                                {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                                  <option key={month} value={month.toString().padStart(2, '0')}>
                                    {month.toString().padStart(2, '0')}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Year
                              </label>
                              <select
                                value={paymentFormData.cc_exp_year}
                                onChange={(e) => setPaymentFormData({...paymentFormData, cc_exp_year: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              >
                                {Array.from({length: 10}, (_, i) => new Date().getFullYear() + i).map(year => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Card Type
                              </label>
                              <select
                                value={paymentFormData.cc_type}
                                onChange={(e) => setPaymentFormData({...paymentFormData, cc_type: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              >
                                <option value="visa">Visa</option>
                                <option value="mastercard">Mastercard</option>
                                <option value="amex">American Express</option>
                                <option value="discover">Discover</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {paymentMethodKind === 'ach' && (
                        <div className="space-y-4">
                          {achInfo ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <Building2 className="h-6 w-6 text-blue-600 mr-3" />
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {achInfo.bank_name || 'Bank Account'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      Account ending in ••{achInfo.account_last_4}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleACHRemove}
                                  disabled={achRemoveLoading}
                                  className="flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  {achRemoveLoading ? 'Removing...' : 'Remove'}
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-3">
                                Verified via Stripe Financial Connections. You can debit this account
                                directly without re-verifying.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm font-medium text-blue-900 mb-2">
                                  Connect your bank account
                                </p>
                                <p className="text-sm text-blue-800">
                                  We use Stripe Financial Connections to securely verify your bank
                                  account. You'll log into your bank in a popup; no routing or
                                  account numbers are stored on our servers.
                                </p>
                              </div>
                              <div className="border border-gray-200 rounded-lg p-4">
                                <p className="text-xs text-gray-700 leading-relaxed">
                                  {NACHA_AUTHORIZATION_TEXT}
                                </p>
                                <div className="flex items-start space-x-3 mt-3">
                                  <input
                                    type="checkbox"
                                    id="achNachaChecked"
                                    checked={achNachaChecked}
                                    onChange={(e) => setAchNachaChecked(e.target.checked)}
                                    className="h-4 w-4 mt-0.5 text-blue-600 rounded border-gray-300"
                                  />
                                  <label htmlFor="achNachaChecked" className="text-sm font-medium text-gray-700">
                                    I agree to the authorization above.
                                  </label>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={handleACHConnect}
                                disabled={!achNachaChecked || achConnectLoading || !stripeInstance}
                                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                              >
                                {achConnectLoading ? (
                                  <div className="flex items-center justify-center">
                                    <LoadingSpinner size="small" />
                                    <span className="ml-2">Opening bank login...</span>
                                  </div>
                                ) : (
                                  'Connect Bank Account'
                                )}
                              </button>
                              {!stripeInstance && (
                                <p className="text-xs text-amber-600">
                                  Stripe is still loading… please wait a moment.
                                </p>
                              )}
                            </div>
                          )}
                          {achPaymentSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                              {achPaymentSuccess}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex space-x-3">
                        <button
                          type="submit"
                          disabled={
                            paymentLoading ||
                            achPayLoading ||
                            (paymentMethodKind === 'ach' && !achInfo)
                          }
                          className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                        >
                          {paymentLoading || achPayLoading ? (
                            <div className="flex items-center justify-center">
                              <LoadingSpinner size="small" />
                              <span className="ml-2">Processing...</span>
                            </div>
                          ) : (
                            `Pay ${formatCurrency(paymentAmount || '0')}${paymentMethodKind === 'ach' ? ' via ACH' : ''}`
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPaymentForm(false)}
                          className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {activeTab === 'payments' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>
                  
                  {paymentsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="large" />
                    </div>
                  ) : payments.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No payment history found</p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Amount
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Payment Method
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Confirmation #
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {payments
                                .slice((paymentsCurrentPage - 1) * itemsPerPage, paymentsCurrentPage * itemsPerPage)
                                .map((payment, index) => (
                                <tr key={payment._id || index} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {payment.created ? new Date(payment.created).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    }) : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    ${Number(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div className="flex items-center">
                                      {payment.payment_type === 'credit_card' && <CreditCard className="h-4 w-4 mr-2 text-gray-400" />}
                                      {payment.payment_type === 'check' && <FileText className="h-4 w-4 mr-2 text-gray-400" />}
                                      {payment.payment_type === 'credit' && <DollarSign className="h-4 w-4 mr-2 text-gray-400" />}
                                      {payment.payment_type === 'manual_ach' && <Receipt className="h-4 w-4 mr-2 text-gray-400" />}
                                      <div className="flex flex-col">
                                        <span className="capitalize font-medium">
                                          {payment.payment_type === 'credit_card' ? 'Credit Card' : 
                                           payment.payment_type === 'credit' ? 'Credit Applied' :
                                           payment.payment_type === 'check' ? 'Check' :
                                           payment.payment_type === 'manual_ach' ? 'Manual ACH' :
                                           payment.payment_type || 'Unknown'}
                                        </span>
                                        {payment.last_4_of_credit_card && (
                                          <span className="text-xs text-gray-500">****{payment.last_4_of_credit_card}</span>
                                        )}
                                        {payment.payment_type === 'manual_ach' && payment.merchant_api_response?.transaction_id && (
                                          <span className="text-xs text-gray-500">ID: {payment.merchant_api_response.transaction_id}</span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      payment.payment_status === 'Paid' 
                                        ? 'bg-green-100 text-green-800'
                                        : payment.payment_status === 'Failed'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {payment.payment_status || 'Unknown'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                                    <div className="flex flex-col">
                                      {payment.confirmation_number && (
                                        <span className="text-xs">{payment.confirmation_number}</span>
                                      )}
                                      {payment.check_number && (
                                        <span className="text-xs text-gray-500 font-medium">Check #{payment.check_number}</span>
                                      )}
                                      {!payment.confirmation_number && !payment.check_number && 'N/A'}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Pagination */}
                      {payments.length > itemsPerPage && (
                        <div className="flex items-center justify-between mt-6">
                          <div className="text-sm text-gray-700">
                            Showing {((paymentsCurrentPage - 1) * itemsPerPage) + 1} to {Math.min(paymentsCurrentPage * itemsPerPage, payments.length)} of {payments.length} payments
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setPaymentsCurrentPage(paymentsCurrentPage - 1)}
                              disabled={paymentsCurrentPage === 1}
                              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-2 text-sm text-gray-700">
                              Page {paymentsCurrentPage} of {Math.ceil(payments.length / itemsPerPage)}
                            </span>
                            <button
                              onClick={() => setPaymentsCurrentPage(paymentsCurrentPage + 1)}
                              disabled={paymentsCurrentPage >= Math.ceil(payments.length / itemsPerPage)}
                              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'orders' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Order History</h3>
                  
                  {ordersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="large" />
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingCart className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No orders found</p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Order Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Order #
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Order Details
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {orders
                                .sort((a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime())
                                .slice((ordersCurrentPage - 1) * itemsPerPage, ordersCurrentPage * itemsPerPage)
                                .map((order, index) => (
                                <tr 
                                  key={order.pk || index} 
                                  className="hover:bg-gray-50 cursor-pointer" 
                                  onClick={() => loadOrderDetails(order.pk)}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {order.date_created ? new Date(order.date_created).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    }) : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    #{order.pk || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    <div className="max-w-xs">
                                      <div className="font-medium">{order.name || 'Order'}</div>
                                      {order.quote_id && (
                                        <div className="text-xs text-gray-500 mt-1">Quote #{order.quote_id}</div>
                                      )}
                                      {order.notes && (
                                        <div className="text-xs text-gray-500 mt-1">{order.notes}</div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                                      order.status === 'completed'
                                        ? 'bg-green-100 text-green-800'
                                        : order.status === 'cancelled'
                                        ? 'bg-red-100 text-red-800'
                                        : order.status === 'new_order'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {order.status === 'new_order' ? 'New Order' : order.status || 'Unknown'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Pagination */}
                      {orders.length > itemsPerPage && (
                        <div className="flex items-center justify-between mt-6">
                          <div className="text-sm text-gray-700">
                            Showing {((ordersCurrentPage - 1) * itemsPerPage) + 1} to {Math.min(ordersCurrentPage * itemsPerPage, orders.length)} of {orders.length} orders
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setOrdersCurrentPage(ordersCurrentPage - 1)}
                              disabled={ordersCurrentPage === 1}
                              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-2 text-sm text-gray-700">
                              Page {ordersCurrentPage} of {Math.ceil(orders.length / itemsPerPage)}
                            </span>
                            <button
                              onClick={() => setOrdersCurrentPage(ordersCurrentPage + 1)}
                              disabled={ordersCurrentPage >= Math.ceil(orders.length / itemsPerPage)}
                              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'settings' && (() => {
                const hasCard = !!billingAccount?.credit_card_info?.last_4;
                const hasAch = !!achInfo;
                const effectiveDefault = getEffectiveDefaultMethod();
                const autoPayOn = !!billingAccount?.automatic_payments;
                const autoPaySubtext = (() => {
                  if (!effectiveDefault) {
                    return 'Add a payment method and mark it as default to enable automatic payments';
                  }
                  if (effectiveDefault === 'card') {
                    return `Automatically charge ${billingAccount?.credit_card_info?.type?.toUpperCase()} ****${billingAccount?.credit_card_info?.last_4} each billing cycle`;
                  }
                  return `Automatically debit ${achInfo?.bank_name || 'your bank account'} ••${achInfo?.account_last_4} each billing cycle`;
                })();

                return (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Automatic Payment Settings</h3>

                    <div className="space-y-6">
                      {/* Saved Payment Methods (unified card + ACH list) */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-900">Saved Payment Methods</h4>
                            <p className="text-gray-600 text-sm mt-1">
                              Mark one as default — auto-pay will use that method each cycle.
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {/* Credit Card Row */}
                          {hasCard ? (
                            <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded">
                                  <CreditCard className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {billingAccount?.credit_card_info?.type?.toUpperCase()} ****{billingAccount?.credit_card_info?.last_4}
                                    </p>
                                    {effectiveDefault === 'card' && (
                                      <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    Expires {billingAccount?.credit_card_info?.exp_month}/{billingAccount?.credit_card_info?.exp_year}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {effectiveDefault !== 'card' && hasAch && (
                                  <button
                                    onClick={() => setDefaultPaymentMethod('card')}
                                    disabled={defaultMethodLoading !== null}
                                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                                  >
                                    {defaultMethodLoading === 'card' ? 'Setting...' : 'Set as default'}
                                  </button>
                                )}
                                <button
                                  onClick={openCardForm}
                                  disabled={cardSetupLoading}
                                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                                >
                                  {cardSetupLoading ? 'Opening...' : 'Update'}
                                </button>
                                {!isKnack && (
                                  <button
                                    onClick={removeCreditCard}
                                    className="px-3 py-1 text-sm text-red-600 hover:text-red-700 transition-colors"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between p-3 bg-white border border-dashed border-gray-300 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-gray-100 rounded">
                                  <CreditCard className="h-5 w-5 text-gray-400" />
                                </div>
                                <p className="text-sm text-gray-500">No credit card on file</p>
                              </div>
                              <button
                                onClick={openCardForm}
                                disabled={cardSetupLoading}
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                              >
                                <CreditCard className="h-4 w-4 mr-1.5" />
                                {cardSetupLoading ? 'Opening...' : 'Add Credit Card'}
                              </button>
                            </div>
                          )}

                          {/* Bank Account (ACH) Row */}
                          {hasAch ? (
                            <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded">
                                  <Building2 className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {achInfo?.bank_name || 'Bank Account'} ••{achInfo?.account_last_4}
                                    </p>
                                    {effectiveDefault === 'ach' && (
                                      <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    Verified via Stripe Financial Connections
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {effectiveDefault !== 'ach' && hasCard && (
                                  <button
                                    onClick={() => setDefaultPaymentMethod('ach')}
                                    disabled={defaultMethodLoading !== null}
                                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                                  >
                                    {defaultMethodLoading === 'ach' ? 'Setting...' : 'Set as default'}
                                  </button>
                                )}
                                <button
                                  onClick={handleACHRemove}
                                  disabled={achRemoveLoading}
                                  className="px-3 py-1 text-sm text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
                                >
                                  {achRemoveLoading ? 'Removing...' : 'Remove'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between p-3 bg-white border border-dashed border-gray-300 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-gray-100 rounded">
                                  <Building2 className="h-5 w-5 text-gray-400" />
                                </div>
                                <p className="text-sm text-gray-500">No bank account connected</p>
                              </div>
                              <button
                                onClick={() => setActiveTab('payment')}
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <Building2 className="h-4 w-4 mr-1.5" />
                                Connect Bank Account
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Auto Payments Toggle */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">Automatic Payments</h4>
                            <p className="text-gray-600 text-sm mt-1">
                              {autoPaySubtext}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {autoPaymentsLoading && (
                              <LoadingSpinner size="small" />
                            )}
                            <label className={`flex items-center cursor-pointer ${
                              !effectiveDefault ? 'opacity-50 cursor-not-allowed' : ''
                            }`}>
                              <input
                                type="checkbox"
                                checked={autoPayOn}
                                onChange={(e) => updateAutoPayments(e.target.checked)}
                                disabled={!effectiveDefault || autoPaymentsLoading}
                                className="sr-only"
                              />
                              <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                autoPayOn ? 'bg-blue-600' : 'bg-gray-300'
                              } ${!effectiveDefault ? 'opacity-50' : ''}`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  autoPayOn ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                              </div>
                            </label>
                          </div>
                        </div>

                        {!effectiveDefault && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center">
                              <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                              <p className="text-sm text-yellow-800">
                                Add a credit card or connect a bank account before enabling automatic payments.
                              </p>
                            </div>
                          </div>
                        )}

                        {autoPayOn && effectiveDefault === 'ach' && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start">
                              <Building2 className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                              <div className="text-sm text-green-800">
                                <p className="font-medium">ACH auto-pay is active.</p>
                                <p className="text-xs mt-0.5">
                                  We'll email you 3 business days before each debit if the amount changes from the prior cycle.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => setActiveTab('payment')}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Make Payment
              </button>
              
              <button
                onClick={() => setActiveTab('statements')}
                className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Statements
              </button>
              
              <button
                onClick={() => setActiveTab('settings')}
                className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Settings className="h-4 w-4 mr-2" />
                Auto Payments
              </button>
            </div>

            {/* Payment Method Info */}
            {billingAccount?.credit_card_info?.last_4 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Method</h4>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded">
                    <CreditCard className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {billingAccount.credit_card_info.type?.toUpperCase()} ****{billingAccount.credit_card_info.last_4}
                    </p>
                    <p className="text-xs text-gray-500">
                      {billingAccount.automatic_payments ? 'Auto-pay enabled' : 'Manual payments'}
                    </p>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Credit Card Modal */}
      {showCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {billingAccount?.credit_card_info?.last_4 ? 'Update Credit Card' : 'Add Credit Card'}
              </h3>
              <button
                onClick={() => setShowCardModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const cardData = {
                first_name: formData.get('first_name'),
                last_name: formData.get('last_name'),
                number: formData.get('number'),
                exp_month: formData.get('exp_month'),
                exp_year: formData.get('exp_year'),
                type: formData.get('type')
              };
              updateCreditCard(cardData);
            }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    defaultValue={billingAccount?.credit_card_info?.first_name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    defaultValue={billingAccount?.credit_card_info?.last_name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Card Number
                </label>
                <input
                  type="text"
                  name="number"
                  placeholder="1234 5678 9012 3456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Month
                  </label>
                  <select
                    name="exp_month"
                    defaultValue={billingAccount?.credit_card_info?.exp_month || '01'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                      <option key={month} value={month.toString().padStart(2, '0')}>
                        {month.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year
                  </label>
                  <select
                    name="exp_year"
                    defaultValue={billingAccount?.credit_card_info?.exp_year || (new Date().getFullYear() + 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {Array.from({length: 10}, (_, i) => new Date().getFullYear() + i).map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    name="type"
                    defaultValue={billingAccount?.credit_card_info?.type || 'visa'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="visa">Visa</option>
                    <option value="mastercard">Mastercard</option>
                    <option value="amex">American Express</option>
                    <option value="discover">Discover</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={cardUpdateLoading}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {cardUpdateLoading && (
                    <LoadingSpinner size="small" className="mr-2" />
                  )}
                  {cardUpdateLoading 
                    ? (billingAccount?.credit_card_info?.last_4 ? 'Updating...' : 'Adding...')
                    : (billingAccount?.credit_card_info?.last_4 ? 'Update Card' : 'Add Card')
                  }
                </button>
                <button
                  type="button"
                  onClick={() => setShowCardModal(false)}
                  disabled={cardUpdateLoading}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Order Details - #{selectedOrder.pk}
              </h3>
              <button
                onClick={() => {
                  setShowOrderModal(false);
                  setSelectedOrder(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {orderDetailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="large" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Order Header */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order Name</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedOrder.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date Created</label>
                      <p className="text-gray-900">
                        {selectedOrder.date_created ? new Date(selectedOrder.date_created).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        selectedOrder.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : selectedOrder.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : selectedOrder.status === 'new_order'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedOrder.status === 'new_order' ? 'New Order' : selectedOrder.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quote ID</label>
                      <p className="text-gray-900">#{selectedOrder.quote_id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
                      <p className="text-gray-900">{selectedOrder.account_id}</p>
                    </div>
                    {selectedOrder.notes && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <p className="text-gray-900">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                {selectedOrder.order_items && selectedOrder.order_items.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h4>
                    <div className="space-y-4">
                      {selectedOrder.order_items.map((item: any, idx: number) => (
                        <div key={item.pk || idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Left Column */}
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                  Product Description
                                </label>
                                <p className="text-sm font-medium text-gray-900">{item.prod_description}</p>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Quantity
                                  </label>
                                  <p className="text-sm text-gray-900">{item.quantity || 'N/A'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Purchase Type
                                  </label>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                    item.purchase_type === 'upgrade' ? 'bg-blue-100 text-blue-800' :
                                    item.purchase_type === 'new_order' ? 'bg-green-100 text-green-800' :
                                    item.purchase_type === 'nrc_product' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {item.purchase_type?.replace('_', ' ') || 'N/A'}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Price
                                  </label>
                                  <p className="text-sm font-medium text-gray-900">
                                    ${Number(item.price || 0).toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Price Per Unit
                                  </label>
                                  <p className="text-sm text-gray-900">
                                    ${Number(item.price_per_unit || 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                  Provision Status
                                </label>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                  item.provision_status === 'provisioned'
                                    ? 'bg-green-100 text-green-800'
                                    : item.provision_status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {item.provision_status || 'Pending'}
                                </span>
                              </div>

                              {item.activation_date && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Activation Date
                                  </label>
                                  <p className="text-sm text-gray-900">
                                    {new Date(item.activation_date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              )}

                              {item.completed !== undefined && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Completed
                                  </label>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    item.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {item.completed ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              )}

                              {item.auto_provision !== undefined && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Auto Provision
                                  </label>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    item.auto_provision ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {item.auto_provision ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              )}

                              {item.taxable !== undefined && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                    Taxable
                                  </label>
                                  <p className="text-sm text-gray-900">
                                    {item.taxable ? `Yes (${item.tax_percent}%)` : 'No'}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Service Snapshot for Upgrades */}
                          {item.account_service_snapshot && (
                            <div className="mt-4 pt-4 border-t border-gray-300">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Previous Service Details</h5>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <div>
                                  <span className="font-medium text-gray-700">Previous Quantity:</span>
                                  <span className="ml-2 text-gray-600">{item.account_service_snapshot.quantity}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Previous Price:</span>
                                  <span className="ml-2 text-gray-600">${Number(item.account_service_snapshot.price || 0).toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Service Status:</span>
                                  <span className="ml-2 text-gray-600">{item.account_service_snapshot.status || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Metadata */}
                          {item.metadata && Object.keys(item.metadata).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-300">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Additional Details</h5>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(item.metadata).map(([key, value]: [string, any]) => (
                                  <span key={key} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded">
                                    <span className="font-medium">{key}:</span>
                                    <span className="ml-1">{String(value)}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!selectedOrder.order_items || selectedOrder.order_items.length === 0) && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <ShoppingCart className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No order items found</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowOrderModal(false);
                  setSelectedOrder(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;